import React, { useEffect, useState } from "react";
import { MIN_TEXT, SECTION_HEADING } from "../utils/homeType";
import { useAuth } from "../context/AuthContext";
import { fetchWeekGames } from "../utils/weekGames";
import { teamLogoUrl } from "../utils/teamLogo";
import { getTeamInitials } from "../utils/teamName";
import { formatPeriod, formatSpreadValue, isGameLive } from "../utils/gameStatus";
import { SCORING_OPENS_AT, isOffSeason } from "../utils/season";

// This week's football, on the general college football page.
//
// Two states from one source. Before kickoff each card is a fixture with its
// time and the expected margin; once a game is live or final the same card
// carries the score. That is the difference between a page worth opening on a
// Saturday and a page that only lists leagues.
//
// It costs almost nothing. fetchWeekGames is the shared, cached reader the
// Scoreboard and Teams tabs already use, keyed by week and not by league, so
// /home is usually served from a cache another tab warmed. Its TTL is already
// two minutes while anything is in progress and a day once a week is done.
//
// The spread is here, the Super Dog mark deliberately is not. The spread
// describes the game. The Super Dog rule describes scoring inside a league,
// and this page sits outside every league and is reachable with no league at
// all, so the mark would name a consequence the reader may have no way to act
// on. It lives on the lineup and the scoreboard instead.

const MAX_CARDS = 12;

// Preseason has no numeric week, so the opening weekend is what to show.
const weekToShow = (currentWeek) => {
  if (isOffSeason(currentWeek)) return 0;
  const parsed = parseInt(String(currentWeek ?? "").replace(/\D/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

// "Thursday 3 September", the day fantasy scoring starts.
const scoringStartLabel = () =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/New_York",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(SCORING_OPENS_AT));

const kickoffLabel = (date) => {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/New_York",
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h12",
  })
    .format(new Date(date))
    .replace(":00", "")
    // "3:30 pm" to "3:30pm". Intl puts a space there and offers no option to
    // remove it. The class matches a narrow or non-breaking space too, since
    // which one ICU emits varies by version and platform.
    .replace(/[\s\u00a0\u202f]+(am|pm)$/i, "$1");
};

// "LIVE · Q4 13:08", or "LIVE · Q2" at the half, where ESPN reports a clock of
// 0:00 and there is no time to show. Falls back to a bare "LIVE" if neither is
// available, which is what a game reports in the seconds around a period break.
//
// The quarter was missing entirely: the ticker showed "LIVE · 13:08", which
// gives no way to tell a first quarter from a fourth.
function liveLabel(game) {
  const detail = [formatPeriod(game.period), game.clock === "0:00" ? "" : game.clock]
    .filter(Boolean)
    .join(" ");
  return detail ? `LIVE · ${detail}` : "LIVE";
}

function Side({ school, score, spread, isLive, played }) {
  const spreadText = formatSpreadValue(spread);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          backgroundColor: "var(--color-bg-subtle)",
          flexShrink: 0,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* FCS and D2 opponents have no file in /public/logos, so the <img>
            below 404s and hides itself. Initials sit underneath rather than
            behind an onError swap, so there's never a blank gray circle. */}
        <span style={{ color: "var(--color-text-muted)", fontSize: MIN_TEXT, fontWeight: 700, letterSpacing: "0.3px", userSelect: "none" }}>
          {getTeamInitials(school)}
        </span>
        <img
          src={teamLogoUrl(school)}
          alt=""
          loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--color-text-strong)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
          minWidth: 0,
        }}
      >
        {school}
      </span>

      {/* The number, not a badge. A badge on the underdog said only that some
          rule applied to it; the spread says how big the mismatch is, which is
          what a manager is actually reading the card for.

          Dropped entirely when there is no line, rather than left as an empty
          column: plenty of games never get one, and 48px of nothing is what
          pushed "Southern Mississippi" into an ellipsis. Both sides of a card
          are derived from the same home_spread, so they always appear and
          disappear together and the two rows cannot fall out of alignment.

          The asterisk sits in its own slot to the LEFT of the number, and that
          slot is always rendered. Appended to the number instead, it was inside
          the right-aligned box and pushed the digits left, so "+29.5*" and
          "-29.5" on the row below did not line up. */}
      {spreadText && (
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "var(--color-text-faint)",
            fontVariantNumeric: "tabular-nums",
            textAlign: "right",
            width: 44,
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {spreadText}
        </span>
      )}

      {played && (
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: isLive ? "var(--color-text-strong)" : "var(--color-text-muted)",
            fontVariantNumeric: "tabular-nums",
            textAlign: "right",
            width: 24,
            flexShrink: 0,
          }}
        >
          {score}
        </span>
      )}
    </div>
  );
}

function ThisWeekBanner() {
  const { seasonConfig, teams } = useAuth();
  const [games, setGames] = useState([]);

  const week = weekToShow(seasonConfig?.currentWeek);
  // Week 0 is the opening weekend. It plays and it counts toward a team's
  // record, but it awards no fantasy points and there is no lineup to set.
  const scores = week > 0;

  useEffect(() => {
    let cancelled = false;

    fetchWeekGames(week)
      .then((rows) => {
        if (cancelled) return;
        const sorted = (rows || [])
          .filter((g) => g.home_team && g.away_team)
          // Live first, then upcoming by kickoff, then finals.
          .sort((a, b) => {
            const rank = (g) => (g.game_complete ? 2 : isGameLive({ gameStatus: g.game_status, period: g.period }) ? 0 : 1);
            const diff = rank(a) - rank(b);
            if (diff !== 0) return diff;
            return new Date(a.date || 0) - new Date(b.date || 0);
          })
          .slice(0, MAX_CARDS);
        setGames(sorted);
      })
      .catch(() => {
        // Additive, like the news and the poll: never block the page.
      });

    return () => { cancelled = true; };
  }, [week]);

  if (!games.length) return null;

  // games.home_team and away_team hold slugs, not school names. The teams map
  // is keyed by the same slug, so it resolves both the display name and the
  // logo. A slug with no entry falls through as written rather than vanishing.
  const schoolFor = (slug) => teams?.[slug]?.school || slug;

  // Saturday looked identical to Tuesday: the header said "WEEK 1" either way,
  // and the only sign anything was happening was a red LIVE line on whichever
  // cards happened to be in view. The row already sorts live games to the
  // front, so it knows.
  const anyLive = games.some((g) => isGameLive({ gameStatus: g.game_status, period: g.period }));

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <h2 style={SECTION_HEADING}>
          {scores ? `Week ${week}` : "Opening Weekend"}
        </h2>

        {/* Same green and the same pulse as the live badge on a lineup row, so
            one thing means live everywhere. */}
        {anyLive && (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
            aria-label="Games in progress"
          >
            <span
              className="animate-pulse"
              style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "var(--color-brand-green)" }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--color-success-text)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Live
            </span>
          </span>
        )}
      </div>

      {/* Said plainly, because the whole page otherwise reads as though the
          season has begun: there are fixtures, there will be live scores, and
          none of it touches a lineup. */}
      {!scores && (
        <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: -4, marginBottom: 10, lineHeight: 1.45 }}>
          These games do not score in Lineup. They count toward each team's
          record, and fantasy scoring starts on {scoringStartLabel()}.
        </div>
      )}

      <div
        className="thisweek-scroll"
        style={{
          display: "flex",
          gap: 10,
          overflowX: "auto",
          paddingBottom: 4,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <style>{`.thisweek-scroll::-webkit-scrollbar { display: none; }`}</style>
        {games.map((game, i) => {
          const live = isGameLive({ gameStatus: game.game_status, period: game.period });
          const played = live || game.game_complete;
          const homeSpread = typeof game.home_spread === "number" ? game.home_spread : null;

          return (
            <div
              key={`${game.home_team}-${game.away_team}-${i}`}
              style={{
                flexShrink: 0,
                width: 226,
                backgroundColor: "var(--color-bg-card)",
                border: "1.5px solid var(--color-bg-subtle)",
                borderRadius: 12,
                padding: "10px 12px",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Side
                  school={schoolFor(game.away_team)}
                  score={game.away_score ?? 0}
                  spread={homeSpread == null ? null : -homeSpread}
                  isLive={live}
                  played={played}
                />
                <Side
                  school={schoolFor(game.home_team)}
                  score={game.home_score ?? 0}
                  spread={homeSpread}
                  isLive={live}
                  played={played}
                />
              </div>

              <div
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: "1px solid var(--color-bg-subtle)",
                  fontSize: 12,
                  color: live ? "var(--color-danger-text)" : "var(--color-text-muted)",
                  fontWeight: live ? 700 : 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {live
                  ? liveLabel(game)
                  : game.game_complete
                    ? "Final"
                    : game.start_time_tbd
                      ? "Time TBC"
                      : kickoffLabel(game.date)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ThisWeekBanner;
